import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { useLottie } from 'lottie-react'

import { Button } from '../../../global/button/Button'
import { useArabicSessionLanguage } from '../../../global/context/SessionSettingsContext'
import { useMixedText, wrapLTR } from '../../../hooks/useMixedText'
import { GiftIcon } from '../../../icons/GiftIcon'
import { LockIcon } from '../../../icons/LockIcon'
import ConfettiLottie from '../../../lotties/Confetti.json'
import { LoyaltyTierDto } from '../../../model/IntroducingBrokerDetailsDto'
import { Text, TextStrong } from '../../../ui/Typography/Typography'
import { getCurrencySymbol } from '../../../utils/transaction.utils'
import { LoyaltyTierMapped } from '../Loyalty'

import styles from './LoyaltyCard.module.scss'

const bronzeSubTierIds = [101, 102, 103]
const bronzeMaxTierId = 1

interface Props {
  tier: LoyaltyTierMapped
  onSubmitReward: (tier?: LoyaltyTierDto | LoyaltyTierMapped | null) => void
}
export const LoyaltyCard: React.FC<Props> = (props) => {
  const { tier, onSubmitReward } = props
  const { t } = useTranslation()
  const { View: Confetti } = useLottie({
    animationData: ConfettiLottie,
    loop: true,
  })

  const isBronzeSubTier = bronzeSubTierIds.includes(tier.id)
  const isBronzeMaxTier = bronzeMaxTierId === tier.id
  const [name, subTier] = isBronzeSubTier && tier.label ? tier.label.split(' ') : []
  const { parseMixedText } = useMixedText()
  const isArabic = useArabicSessionLanguage()

  function formatRewardAmount(amount: number): string {
    if (amount >= 10000) {
      return parseMixedText(amount.toString()).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    }
    return amount.toString()
  }

  const isActionButtonDisabled =
    tier.isRejected ||
    tier.isCreated ||
    tier.isRedeemed ||
    (!tier.isApproved && !tier.isRejected && !tier.isCreated && !tier.isRedeemed)

  const buttonContent = useMemo(() => {
    if (tier.isRedeemed) {
      return t('Claimed')
    }

    if (tier.isRejected || tier.isCreated || (!tier.isApproved && !tier.isRedeemed)) {
      return <LockIcon color='alwaysWhite' />
    }

    if (tier.isApproved) {
      return <GiftIcon color='alwaysWhite' />
    }

    return <LockIcon color='alwaysWhite' />
  }, [t, tier.isApproved, tier.isCreated, tier.isRedeemed, tier.isRejected])

  const tierPrizeText = isArabic
    ? wrapLTR(`${getCurrencySymbol('USD')} ${formatRewardAmount(tier.rewardAmount)}`)
    : `${getCurrencySymbol('USD')} ${formatRewardAmount(tier.rewardAmount)}`

  return (
    <article
      className={classNames(styles.loyaltyCard, {
        [styles.active]: tier.isApproved,
        [styles.completed]: tier.isRedeemed,
      })}
      {...(tier.label && { 'aria-label': tier.label })}
    >
      {tier.isApproved && <div className={styles.confetti}>{Confetti}</div>}
      <header
        className={classNames(styles.topSection, {
          [styles.noLabel]: !isBronzeSubTier && !isBronzeMaxTier,
        })}
      >
        {tier.imageUri && (
          <img
            src={tier.imageUri}
            alt={tier.label || 'loyalty tier icon'}
            width={48}
            height={48}
            loading='lazy'
          />
        )}

        <div className={styles.title}>
          <Text>{isBronzeSubTier ? name : tier.label}</Text>
          {isBronzeSubTier && <Text>{`${t('IB.MultiTier.Sub Level')} ${subTier}`}</Text>}
          {!isBronzeSubTier && isBronzeMaxTier && <Text>{t('IB.MultiTier.Sub Level')} 4/4</Text>}
        </div>

        <section className={styles.prize}>
          <GiftIcon />
          <TextStrong>{tierPrizeText}</TextStrong>
        </section>
      </header>

      <Button.New
        disabled={isActionButtonDisabled}
        appearance='primary'
        size='S'
        fullWidth
        className={styles.button}
        onClick={() => onSubmitReward(tier)}
        aria-disabled={isActionButtonDisabled}
        aria-label={
          tier.isApproved
            ? t('IB.Dashboard.Claim Rewards')
            : tier.isRedeemed
            ? t('Claimed')
            : t('Locked')
        }
      >
        {buttonContent}
      </Button.New>
    </article>
  )
}
